/**
 * Frontmatter is the block between a `---` line at the very top of the file
 * and the next `---` line. Only that leading block is metadata: a `---`
 * further down the body is a horizontal rule (or a stray line) and must stay
 * in the body. Splitting the whole file on '---' used to turn those into a
 * second "frontmatter" and print raw `key: value` lines above the article.
 */
const LEADING_BLOCK = /^﻿?---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

/** Split raw markdown into its frontmatter text (or null) and the body. */
export function splitFrontmatter(raw) {
	const text = String(raw || "");
	const m = LEADING_BLOCK.exec(text);
	if (!m) return { frontmatter: null, body: text };
	return { frontmatter: m[1], body: text.slice(m[0].length) };
}

/**
 * Parse a frontmatter block into plain string values. Quoted scalars lose
 * their quotes; anything else is kept verbatim for the caller to coerce.
 */
export function parseFrontmatterLines(frontmatter) {
	const meta = {};
	if (!frontmatter) return meta;
	for (const line of frontmatter.split("\n")) {
		const idx = line.indexOf(":");
		if (idx === -1) continue;
		const key = line.slice(0, idx).trim();
		if (!key || key.startsWith("#")) continue;
		meta[key] = line
			.slice(idx + 1)
			.trim()
			.replace(/^"(.*)"$/, "$1");
	}
	return meta;
}

/** `{ meta, body }` for the common case: string metadata, trimmed body. */
export function parseFrontmatter(raw) {
	const { frontmatter, body } = splitFrontmatter(raw);
	return {
		meta: parseFrontmatterLines(frontmatter),
		body: body.trim(),
		hasFrontmatter: frontmatter !== null,
	};
}
